import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Globe, Timer } from 'lucide-react';
import { WebsiteSignalEvent } from '@/types/signals';
import { formatTime } from '@/app/lib/utils';
import { useTranslations } from 'next-intl';
import Pill from '../ui/Pill';
import { getAttributionPresentation } from './attributionPresentation';

import { SIGNAL_ACTION_TRANSLATION_KEYS } from './signalFilterConfigs';

type WebsiteSignalActivityItemProps = {
  signal: WebsiteSignalEvent;
  expanded?: boolean;
};

const formatDuration = (seconds: number) => {
  if (!seconds || seconds < 1) return '';
  const rounded = Math.round(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remainingSeconds = rounded % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const WebsiteSignalActivityItem = React.memo(
  ({ signal, expanded = false }: WebsiteSignalActivityItemProps) => {
    const t = useTranslations('signals');
    const [copied, setCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const websiteSignal = signal.website;
    const targetPage = websiteSignal.target_page;
    const targetPageName = targetPage?.short_name?.trim() || targetPage?.name?.trim() || '';
    const attributionPresentation = getAttributionPresentation(websiteSignal.attribution);
    const attributionSourceName = attributionPresentation?.sourceName || '';
    const attributionSourceLogo = attributionPresentation?.sourceLogo || '';
    const duration = formatDuration(websiteSignal.session.duration_secs);
    const activityAt =
      websiteSignal.page?.visited_at || signal.signal_at || signal.captured_at || signal.created_at;
    const url = websiteSignal.url.raw;
    const action = signal.action || websiteSignal.trigger || '';
    const actionTranslationKey = SIGNAL_ACTION_TRANSLATION_KEYS[action];
    const actionLabel = actionTranslationKey ? t(actionTranslationKey as never) : '-';

    const href = url.startsWith('http') ? url : `https://${url}`;

    useEffect(
      () => () => {
        if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      },
      []
    );

    const handleCopy = async () => {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    };

    if (expanded) {
      return (
        <div className="grid grid-cols-[105px_minmax(0,1fr)_24px] gap-x-3 border-b border-border px-4 py-3 text-[11px] last:border-b-0">
          <span className="text-muted-foreground">
            {formatTime(activityAt)}
            {duration ? ` - ${duration}` : ''}
          </span>
          <div className="min-w-0">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-secondary-foreground [overflow-wrap:anywhere] hover:underline"
            >
              {url}
            </a>
            {(targetPageName || attributionSourceName) && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground">
                {targetPageName && (
                  <div className="flex items-center gap-2">
                    <Check size={12} className="text-success-base" />
                    <span>Target Page</span>
                    <Pill variant="outline" color="purple" rounded={false} className="uppercase">
                      <Globe size={12} strokeWidth={1.5} />
                      {targetPageName}
                    </Pill>
                  </div>
                )}
                {attributionSourceName && (
                  <Pill variant="outline" className="gap-1.5 px-2">
                    {attributionSourceLogo && (
                      <img
                        src={attributionSourceLogo}
                        alt=""
                        className="size-3.5 object-contain"
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    <span>{attributionSourceName}</span>
                  </Pill>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label={copied ? 'Copied' : 'Copy URL'}
            title={copied ? 'Copied' : 'Copy URL'}
            className="flex size-6 items-center justify-center text-muted-foreground hover:text-foreground"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              void handleCopy();
            }}
          >
            {copied ? <Check size={14} className="text-success-base" /> : <Copy size={14} />}
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2 border-b border-border p-3 text-[11px] last:border-b-0">
        <div className="flex items-center gap-1">
          <span className="shrink-0 text-muted-foreground">{formatTime(activityAt)} -</span>
          <span className="shrink-0 text-muted-foreground">{actionLabel}</span>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 flex-1 truncate font-medium text-secondary-foreground hover:underline"
            title={url}
          >
            {url}
          </a>
          {duration && (
            <span className="flex shrink-0 items-center gap-1 text-sidebar-ring">
              <Timer size={12} />
              {duration}
            </span>
          )}
        </div>
        {(targetPageName || attributionSourceName) && (
          <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
            {targetPageName && (
              <div className="flex items-center gap-2">
                <Check size={12} className="text-success-base" />
                <span>Target Page</span>
                <Pill variant="outline" color="purple" rounded={false} className="uppercase">
                  <Globe size={12} strokeWidth={1.5} />
                  {targetPageName}
                </Pill>
              </div>
            )}
            {attributionSourceName && (
              <Pill variant="outline" className="gap-1.5 px-2">
                {attributionSourceLogo && (
                  <img
                    src={attributionSourceLogo}
                    alt=""
                    className="size-3.5 object-contain"
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <span>{attributionSourceName}</span>
              </Pill>
            )}
          </div>
        )}
      </div>
    );
  }
);

WebsiteSignalActivityItem.displayName = 'WebsiteSignalActivityItem';

export default WebsiteSignalActivityItem;
